import { MRP_LOCATIONS, type MrpAmlEntry } from "@/lib/mrp";
import {
  formatAmlAlternateLabel,
  isPackSizeAmlRow,
} from "@/pages/mrp/aml/amlEntryUtils";
import type { SkuFormValues } from "@/pages/mrp/skuPropertiesFormTypes";

type DirtyFields = Partial<Record<keyof SkuFormValues, boolean | Partial<Record<string, boolean>>>>;

function isTopLevelDirty(dirtyFields: DirtyFields, key: keyof SkuFormValues): boolean {
  return dirtyFields[key] === true;
}

function nestedLocationDirty(
  dirtyFields: DirtyFields,
  key: "mrp_reserve_by_location" | "mrp_replenish_to_by_location",
  prefix: string,
): string[] {
  const nested = dirtyFields[key];
  if (nested == null || typeof nested !== "object") {
    return [];
  }
  const labels: string[] = [];
  for (const { code, label } of MRP_LOCATIONS) {
    if (nested[code] === true) {
      labels.push(`${prefix} (${label})`);
    }
  }
  return labels;
}

/** Human-readable labels for react-hook-form dirty fields and AML pack drafts. */
export function skuPropertiesDirtyFieldLabels(
  dirtyFields: DirtyFields,
  amlRows: MrpAmlEntry[],
  amlPackDrafts: Record<number, string>,
  cpn: string,
): string[] {
  const labels: string[] = [];

  if (isTopLevelDirty(dirtyFields, "sku")) labels.push("SKU code");
  if (isTopLevelDirty(dirtyFields, "planning_mode")) labels.push("Planning mode");
  labels.push(...nestedLocationDirty(dirtyFields, "mrp_reserve_by_location", "Reserve"));
  labels.push(...nestedLocationDirty(dirtyFields, "mrp_replenish_to_by_location", "Replenish to"));
  if (isTopLevelDirty(dirtyFields, "serialize")) labels.push("Serialize");
  if (isTopLevelDirty(dirtyFields, "template_id")) labels.push("Template ID");
  if (isTopLevelDirty(dirtyFields, "manufacture")) labels.push("Manufacture");
  if (isTopLevelDirty(dirtyFields, "for_sale")) labels.push("For sale");
  if (isTopLevelDirty(dirtyFields, "advertised")) labels.push("Advertised");
  if (isTopLevelDirty(dirtyFields, "service")) labels.push("Service");
  if (isTopLevelDirty(dirtyFields, "build_to_order")) labels.push("Build to order");
  if (isTopLevelDirty(dirtyFields, "for_sale_deployed")) labels.push("For sale deployed");
  if (isTopLevelDirty(dirtyFields, "obsolete")) labels.push("Obsolete");
  if (isTopLevelDirty(dirtyFields, "under_engineering_review")) labels.push("Under engineering review");
  if (isTopLevelDirty(dirtyFields, "cots")) labels.push("Commercial off the shelf");
  if (isTopLevelDirty(dirtyFields, "erp_skip_import")) labels.push("ERP skip import");
  if (isTopLevelDirty(dirtyFields, "mv_description")) labels.push("Megaventory description");
  if (isTopLevelDirty(dirtyFields, "mv_variant")) labels.push("Megaventory variant");
  if (isTopLevelDirty(dirtyFields, "mv_category_id")) labels.push("Megaventory category");
  if (isTopLevelDirty(dirtyFields, "mv_product_type")) labels.push("Megaventory product type");
  if (isTopLevelDirty(dirtyFields, "mv_sellable")) labels.push("Sellable");
  if (isTopLevelDirty(dirtyFields, "mv_purchasable")) labels.push("Purchasable");
  if (isTopLevelDirty(dirtyFields, "mv_unit")) labels.push("Unit of measure");
  if (isTopLevelDirty(dirtyFields, "mv_image_url")) labels.push("Image URL");
  if (isTopLevelDirty(dirtyFields, "mv_supply_chain_notes")) labels.push("Megaventory comments");
  if (isTopLevelDirty(dirtyFields, "hub_supply_chain_notes")) labels.push("Hub supply chain notes");
  if (isTopLevelDirty(dirtyFields, "fair_market_value")) labels.push("Fair market value");
  if (isTopLevelDirty(dirtyFields, "mv_hts_code")) labels.push("HTS code");

  for (const row of amlRows) {
    if (!isPackSizeAmlRow(row)) continue;
    const draft = (amlPackDrafts[row.id] ?? "").trim();
    const previous =
      row.standard_pack_size != null && row.standard_pack_size >= 1
        ? String(row.standard_pack_size)
        : "";
    if (draft !== previous) {
      labels.push(`Pack size (${formatAmlAlternateLabel(row, cpn)})`);
    }
  }

  return labels;
}

export function formatSkuPropertiesDirtyHint(labels: readonly string[]): string | null {
  if (labels.length === 0) {
    return null;
  }
  return labels.map((label) => `• ${label}`).join("\n");
}
